import {
  collection,
  deleteField,
  doc,
  getDoc,
  runTransaction,
  Timestamp,
} from "firebase/firestore";

/** `users/{visitOwnerUid}/Visits/{merchantId}/events` — visitOwnerUid is Firebase Auth UID on web. */
function userVisitEventCollectionRef(db, visitOwnerUid, merchantId) {
  return collection(db, "users", visitOwnerUid, "Visits", merchantId, "events");
}

/**
 * `users/{visitOwnerUid}/Visits/{merchantId}` — one document per (user, merchant) with
 * `visit_count`, `last_visit_at`, etc. On web, `visitOwnerUid` is the Firebase Auth uid
 * (anonymous until phone verification).
 */
export function getUserVisitDocRef(db, visitOwnerUid, merchantId) {
  return doc(db, "users", visitOwnerUid, "Visits", merchantId);
}

/** @deprecated use getUserVisitDocRef */
export const getVisitAggregateRef = getUserVisitDocRef;

export const USERS_COL = "users";
export const USER_VISITS_SUB = "Visits";
export const USER_VISIT_EVENTS_SUB = "events";

/** Do not count another visit for the same device+merchant within this window. */
export const VISIT_COOLDOWN_MS = 60 * 1000;

/** `/?m=` merchant deep links: at most one credited visit per this window (refresh). */
export const MERCHANT_LINK_VISIT_COOLDOWN_MS = 60 * 1000;

/** @param {unknown} ts @returns {number | null} */
function toMillisSafe(ts) {
  if (!ts || typeof ts !== "object") return null;
  const t = /** @type {{ toMillis?: () => number; seconds?: number }} */ (ts);
  if (typeof t.toMillis === "function") return t.toMillis();
  if (typeof t.seconds === "number") return t.seconds * 1000;
  return null;
}

/** Read credited visit total from aggregate doc (canonical: `visit_count`). */
export function readVisitCountFromAggregate(data) {
  if (!data || typeof data !== "object") return 0;
  const n = data.visit_count ?? data.visitCount ?? data.visits;
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

/** @param {import("firebase/firestore").Firestore} db */
export async function getDeviceMerchantVisitInfo(db, deviceId, merchantId) {
  const agRef = getUserVisitDocRef(db, deviceId, merchantId);
  const snap = await getDoc(agRef);
  if (!snap.exists()) {
    return { exists: false, visits: 0 };
  }
  const data = snap.data();
  return { exists: true, visits: readVisitCountFromAggregate(data) };
}

/**
 * Atomically applies cooldown, optional visit cap, increments visit_count, and appends
 * one row to `users/{deviceId}/Visits/{merchantId}/events/{autoId}`.
 * @param {import("firebase/firestore").Firestore} db
 * @param {object} args
 * @param {number} [args.cooldownMs] — defaults to {@link VISIT_COOLDOWN_MS}; use {@link MERCHANT_LINK_VISIT_COOLDOWN_MS} for `/?m=` links.
 */
export async function recordCreditedDeviceVisit(db, {
  deviceId,
  merchantId,
  userId: optionalUserId,
  tagId,
  merchantName,
  rewardThreshold,
  /** When set to a finite number, no more credited visits once count reaches this. Omit for no cap. */
  visitCapThreshold,
  visitsEarned,
  cooldownMs: cooldownMsArg,
  tapToken,
}) {
  if (!deviceId || !merchantId) {
    throw new Error("recordCreditedDeviceVisit: deviceId and merchantId required");
  }
  const hasCap =
      visitCapThreshold != null && Number.isFinite(visitCapThreshold);
  const cooldownMs = cooldownMsArg ?? VISIT_COOLDOWN_MS;
  return runTransaction(db, async (t) => {
    // Use an explicit timestamp so Firestore rules see a concrete `timestamp` type.
    // (Server transforms like serverTimestamp() can be tricky to validate in security rules.)
    const nowTs = Timestamp.now();

    const agRef = getUserVisitDocRef(db, deviceId, merchantId);
    const agSnap = await t.get(agRef);

    // First write for this (user, merchant) pair? Used to decide whether to
    // initialize `reward_claim_requested`. On subsequent writes we must NOT
    // touch that flag: a user may have requested a reward claim (flag = true),
    // and the redemption tap increments visit_count in this same write path.
    // The `onUserVisitWrite` cloud function (transitionedToClaimedViaNfc) needs
    // the flag to still be true on the *after* state to apply the claim. If we
    // reset it to false here, the claim would never fire. The cloud function is
    // the only place that clears the flag (after applying the reward).
    const isFirstWrite = !agSnap.exists();

    // Validate tap token if provided
    let tokenRef = null;
    if (tapToken) {
      tokenRef = doc(db, "tapTokens", tapToken);
      const tokenSnap = await t.get(tokenRef);
      if (!tokenSnap.exists()) {
        return { status: "invalid_token", visits: 0 };
      }
      const tokenData = tokenSnap.data();
      if (tokenData.used) {
        return { status: "token_used", visits: 0 };
      }
      if (tokenData.expiresAt.toMillis() < Date.now()) {
        return { status: "token_expired", visits: 0 };
      }
    }

    const data = agSnap.exists() ? agSnap.data() : {};
    const currentVisits = readVisitCountFromAggregate(data);
    const lastVisitAt = data.last_visit_at ?? data.lastVisitAt;

    if (hasCap && currentVisits >= visitCapThreshold) {
      return { status: "cap_reached", visits: currentVisits };
    }

    const lastMillis = toMillisSafe(lastVisitAt);
    if (lastMillis != null) {
      const now = Date.now();
      const elapsed = now - lastMillis;
      if (elapsed < cooldownMs) {
        return {
          status: "cooldown",
          visits: currentVisits,
          cooldownRemainingMs: Math.max(0, cooldownMs - elapsed),
        };
      }
    }

    const newVisits = currentVisits + visitsEarned;

    // Mark token as used atomically with the visit write
    if (tokenRef) {
      t.update(tokenRef, { used: true });
    }

    t.set(
        agRef,
        {
          device_id: deviceId,
          merchant_id: merchantId,
          merchantId,
          ...(merchantName ? { merchantName } : {}),
          visit_count: newVisits,
          visitCount: deleteField(),
          visits: deleteField(),
          last_visit_at: nowTs,
          lastVisitAt: nowTs,
          last_tag_id: tagId,
          tag_id: tagId,
          updated_at: nowTs,
          updatedAt: nowTs,
          reward_threshold: rewardThreshold,
          // Only initialize the claim flag when the doc is first created. On
          // later writes, leave whatever value is there so a pending claim
          // survives the redemption tap (see isFirstWrite note above).
          ...(isFirstWrite ? { reward_claim_requested: false } : {}),
        },
        { merge: true }
    );

    const eventRef = doc(userVisitEventCollectionRef(db, deviceId, merchantId));
    t.set(eventRef, {
      device_id: deviceId,
      merchant_id: merchantId,
      tag_id: tagId,
      timestamp: nowTs,
      ...(optionalUserId != null && optionalUserId !== ""
          ? { user_id: optionalUserId }
          : {}),
    });

    return { status: "success", visits: newVisits };
  });
}
